import axios, { AxiosResponse } from 'axios'
import jwt_decode, { JwtPayload } from 'jwt-decode'
import { notify } from '../useNotifications'
import { i18n } from '../../localization'

const apiTimeout = 100000

export const api = axios.create({
    baseURL: getApiBaseUrl(),
    timeout: apiTimeout,
})

export const apiWithoutAuth = axios.create({
    baseURL: getApiBaseUrl(),
    timeout: apiTimeout,
})

function getApiBaseUrl() {
    return window.condaApiEndpoint
}

export type ApiRes<A = unknown> = Promise<AxiosResponse<A>>

export function getUserTokenCookie(): string {
    return document.cookie.split('; ').find((row) => row.startsWith('conda_user_token='))
}

export function isUserLoggedIn(): boolean {
    return getUserTokenCookie() !== undefined
}

// The current implementation authorizes through a wordpress request that fetches a valid bearer token
// TODO: We should also reevaluate this in the future and probably renew the token directly via keycloak
api.interceptors.request.use(
    async (config) => {
        let token

        const cookieString = getUserTokenCookie()

        const decoded = cookieString ? jwt_decode<JwtPayload>(cookieString.split('=')[1]) : null

        // Check if token that is saved in cookie is still valid for more than 1 minute
        if (decoded && decoded.exp && new Date(decoded.exp * 1000) > new Date(Date.now() + 60000)) {
            token = cookieString.split('=')[1]
        } else {
            // fetch and save auth tokens from wordpress
            const bodyFormData = new FormData()
            bodyFormData.append('action', 'conda_get_user_token_ajax_frontend_v2')

            const response = await axios.post('/wp-admin/admin-ajax.php', bodyFormData, {})

            if (response.data == 0 || response.status !== 200) {
                if (window.condaKeycloakLoginLink) {
                    window.location.href = window.condaKeycloakLoginLink
                } else {
                    window.location.reload()
                }
                return
            }

            window.createCookie(
                'conda_user_token',
                response.data.conda_user_token,
                response.data.conda_user_token_expiration
            )
            window.createCookie(
                'conda_user_profile',
                response.data.conda_user_profile,
                response.data.conda_user_profile_expiration
            )
            window.createCookie(
                'conda_refresh_token',
                response.data.conda_refresh_token,
                response.data.conda_refresh_token_expiration
            )

            token = response.data.conda_user_token
        }

        config.headers.Authorization = `Bearer ${token}`

        if (window.condaCurrentLanguage) {
            config.headers['x-conda-frontend-language'] = window.condaCurrentLanguage
        }

        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

// General Laravel Error Message Catching
api.interceptors.response.use(undefined, (error) => {
    if (error?.response?.status === 422) {
        // Note: ValidationException in API
        const message = error.response.data.errors
            .map((error) => {
                return error.message
            })
            .join('\n')

        notify('Error', i18n.global.t('errors.validation_error'), message)
    } else if (
        error?.response?.status === 403 ||
        (error?.response?.status === 400 &&
            error?.response?.data?.error === true &&
            error?.response?.data?.errorMessage !== undefined)
    ) {
        // Note: ResponseException in API
        notify('Error', i18n.global.t('errors.general_error'), error.response.data.errorMessage)
    }
    return Promise.reject(error)
})
